"use client";

import { LANDING_CONTAINER_CLASS } from "@/components/landing/constants";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Input,
  Textarea,
} from "@restomat/components/ui";
import {
  Button,
  Field,
  FieldError,
  FieldLabel,
} from "@restomat/components/client/ui";
import { Mail, Phone } from "lucide-react";
import PeaceIllustration from "@/components/graphics/illustrations/peace-illustration";
import { cn } from "@restomat/shared-utils/utils";
import { Suspense, useCallback, useMemo, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { useForm, type Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { useTranslations } from "next-intl";
import { postApiMainV1PublicContactsSubmitMutation } from "@restomat/api/tanstack";
import {
  trackClickEmail,
  trackClickPhone,
  trackFormStart,
  trackSubmitInquiry,
} from "@/lib/analytics";
import {
  getStoredBilling,
  getStoredPackages,
} from "@/utils/landing-storage";
import { PRICING_TIERS } from "@/components/landing/pricing-data";
import { motion, useInView } from "framer-motion";
import { fadeInUp, staggerContainer } from "./animations";

type ContactFormValues = {
  name: string;
  email: string;
  phone?: string;
  message?: string;
  web?: string;
};

function ContactForm({
  isInAdmin = false,
}: {
  isInAdmin?: boolean;
}) {
  const t = useTranslations("landing.contact");
  const tPricing = useTranslations("landing.pricing");

  const contactFormSchema = useMemo(
    () =>
      z.object({
        name: z.string().min(1, t("form.nameRequired")),
        email: z
          .email(t("form.emailInvalid"))
          .min(1, t("form.emailRequired")),
        phone: z.string().optional(),
        message: z.string().optional(),
        web: z.string().optional(),
      }),
    [t],
  );

  const form = useForm<ContactFormValues>({
    // Two zod copies resolve in this workspace (the schema is built with zod
    // 4.1.x while @hookform/resolvers' overloads expect 4.3.x), so the call
    // doesn't type-match. Bridge with a cast — the real schema is passed through
    // at runtime, so validation behavior is unchanged.
    resolver: zodResolver(
      contactFormSchema as never,
    ) as Resolver<ContactFormValues>,
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      message: "",
      web: "",
    },
  });

  const submitFormMutation = useMutation({
    ...postApiMainV1PublicContactsSubmitMutation(),
    onSuccess: () => {
      toast.success(t("form.success"));
      form.reset();
    },
    onError: () => {
      toast.error(t("form.error"));
    },
  });

  const formStartTracked = useRef(false);
  const handleFormStart = useCallback(() => {
    if (!formStartTracked.current) {
      formStartTracked.current = true;
      trackFormStart("contact");
    }
  }, []);

  const onSubmit = (values: ContactFormValues) => {
    const source = isInAdmin ? "admin" : "landing";

    // Value of the tier the visitor picked in the calculator, if any.
    const billing = getStoredBilling();
    const selectedTier = PRICING_TIERS.find((p) =>
      getStoredPackages().includes(p.id),
    );

    const value = selectedTier ? selectedTier.price[billing] : 0;
    const items = selectedTier ? [tPricing(selectedTier.titleKey)] : [];

    trackSubmitInquiry(value, items, billing, source);

    submitFormMutation.mutate({
      body: {
        name: values.name,
        email: values.email,
        phone: values.phone,
        message: values.message,
        web: values.web,
        source: source,
      },
    });
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} onFocus={handleFormStart} className="w-full md:flex-1">
    <Card
      className={"w-full md:min-w-[600px] md:w-auto md:flex-1"}
      variant={"light"}
    >
      <CardHeader>
        <CardTitle className={"text-leading-sm-bold"}>{t("form.title")}</CardTitle>
        <p className={"mt-4 text-sm md:text-base"}>{t("form.subtitle")}</p>
      </CardHeader>
      <CardContent className={"flex flex-col gap-6"}>
        <div className="flex flex-col gap-2">
          <Field data-invalid={!!form.formState.errors.name}>
            <FieldLabel htmlFor="contact-name">{t("form.name")}</FieldLabel>
            <Input
              id="contact-name"
              {...form.register("name")}
              required={true}
              aria-invalid={!!form.formState.errors.name}
            />
            <FieldError errors={[form.formState.errors.name]} />
          </Field>
          <Field data-invalid={!!form.formState.errors.email}>
            <FieldLabel htmlFor="contact-email">{t("form.email")}</FieldLabel>
            <Input
              id="contact-email"
              {...form.register("email")}
              required={true}
              aria-invalid={!!form.formState.errors.email}
            />
            <FieldError errors={[form.formState.errors.email]} />
          </Field>
          <Field data-invalid={!!form.formState.errors.phone}>
            <FieldLabel htmlFor="contact-phone">{t("form.phone")}</FieldLabel>
            <Input
              id="contact-phone"
              {...form.register("phone")}
              aria-invalid={!!form.formState.errors.phone}
            />
            <FieldError errors={[form.formState.errors.phone]} />
          </Field>
          <Field data-invalid={!!form.formState.errors.web}>
            <FieldLabel htmlFor="contact-web">{t("form.web")}</FieldLabel>
            <Input
              id="contact-web"
              {...form.register("web")}
              aria-invalid={!!form.formState.errors.web}
            />
            <FieldError errors={[form.formState.errors.web]} />
          </Field>
          <Field data-invalid={!!form.formState.errors.message}>
            <FieldLabel htmlFor="contact-message">{t("form.message")}</FieldLabel>
            <Textarea
              id="contact-message"
              {...form.register("message")}
              aria-invalid={!!form.formState.errors.message}
            />
            <FieldError errors={[form.formState.errors.message]} />
          </Field>
        </div>
      </CardContent>
      <CardFooter className={"w-full flex flex-col items-center md:items-end"}>
        <Button
          variant={"secondary"}
          size={"sm"}
          type="submit"
          isLoading={submitFormMutation.isPending}
        >
          {t("form.submit")}
        </Button>
      </CardFooter>
    </Card>
    </form>
  );
}

export default function ContactSection({
  isInAdmin = false,
}: {
  isInAdmin?: boolean;
}) {
  const t = useTranslations("landing.contact");
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: "some" });

  return (
    <div className={"relative max-w-full"}>
      {/* Desktop PeaceIllustration uses opacity-only to avoid position jumping */}
      {!isInAdmin && (
        <motion.div
          className="z-[80] h-[32vh] w-auto absolute hidden md:block bottom-0 right-0"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <PeaceIllustration className="h-full w-auto" />
        </motion.div>
      )}
      <section
        ref={sectionRef}
        id="contact"
        className={`${!isInAdmin && "border-t-4 border-black bg-foreground pt-16"}`}
      >
        <motion.div
          className={cn("w-full", !isInAdmin && LANDING_CONTAINER_CLASS)}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
        >
          <div className="space-y-6 w-full">
            <motion.h2 className="text-secondary" variants={fadeInUp}>
              {t("heading")}
            </motion.h2>

            <motion.div
              className={"flex flex-col md:flex-row gap-8 items-start"}
              variants={fadeInUp}
            >
              <Suspense fallback={<div>{t("loading")}</div>}>
                <ContactForm isInAdmin={isInAdmin} />
              </Suspense>

              <Card className={"flex-1 w-full md:w-auto"} variant={"light"}>
                <CardHeader>
                  <CardTitle className={"font-headings text-h2"}>
                    {t("infoTitle")}
                  </CardTitle>
                  <div className={"text-leading-sm-bold text-primary"}>
                    {t("infoSubtitle")}
                  </div>
                </CardHeader>
                <CardContent className={"space-y-4"}>
                  <a
                    href={"mailto:ahoj@restomat.cz"}
                    onClick={() => trackClickEmail()}
                    className={
                      "md:text-lg px-3 py-1 rounded-xl border-2 border-foreground flex justify-between items-center transition hover:bg-foreground hover:text-white"
                    }
                  >
                    <span>ahoj@restomat.cz</span>
                    <Mail />
                  </a>
                  <a
                    href={"tel:+420518888255"}
                    onClick={() => trackClickPhone()}
                    className={
                      "md:text-lg px-3 py-1 rounded-xl border-2 border-foreground flex justify-between items-center transition hover:bg-foreground hover:text-white"
                    }
                  >
                    <span>+420 518 888 255</span>
                    <Phone />
                  </a>
                </CardContent>
              </Card>
              <div className={"hidden xl:block 2xl:w-16 3xl:w-24"}></div>
            </motion.div>
          </div>
        </motion.div>

        {!isInAdmin && (
          <div className={"relative w-full h-[10rem] md:h-8"}>
            {/* Mobile PeaceIllustration uses opacity-only to avoid position jumping */}
            <motion.div
              className="z-[80] w-1/2 h-auto block md:hidden right-0 bottom-0 absolute"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : { opacity: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <PeaceIllustration className="w-full h-auto" />
            </motion.div>
          </div>
        )}
      </section>
    </div>
  );
}
